// ─── App-wide config ─────────────────────────────────────────────────────────
//
// SITE_URL is the public web origin where the hosted Nomad site lives
// (the React build deployed to Vercel and served at https://nomadmktplace.com).
//
// It is used as the email-confirmation redirect target. Supabase sends the
// confirmation email; clicking the link hits Supabase's verify endpoint, which
// then redirects the browser to this URL. Because the email always opens in a
// browser — even when signup happened inside the native app — this must point at
// a real hosted page, NOT localhost and NOT the in-app capacitor:// origin.
//
// Override per-environment with REACT_APP_SITE_URL (e.g. a Vercel preview URL or
// http://localhost:3000 during local dev).
export const SITE_URL =
  process.env.REACT_APP_SITE_URL || 'https://nomadmktplace.com';

// Landing page Supabase redirects to after a successful email confirmation.
// Must be added to Supabase → Authentication → URL Configuration → Redirect URLs.
export const EMAIL_CONFIRM_REDIRECT = `${SITE_URL}/email-confirmed`;

// Landing page Supabase redirects to when a user clicks the password-reset link
// in the recovery email. Supabase validates the recovery token server-side, then
// redirects here with a short-lived recovery session so the user can set a new
// password. Like EMAIL_CONFIRM_REDIRECT this opens in a browser (even from the
// native app), so it must be a real hosted page — and must ALSO be added to
// Supabase → Authentication → URL Configuration → Redirect URLs.
export const RESET_PASSWORD_REDIRECT = `${SITE_URL}/reset-password`;

// ─── Paid features switch ────────────────────────────────────────────────────
//
// Gates every in-app purchase entry point (Listing Boost, Like Notes, Listing
// Alerts, Blast). ON by default so every build — including TestFlight — shows
// the purchase UI. Set REACT_APP_PAYMENTS_ENABLED=false to hide it (e.g. if a
// specific App Store submission needs to ship without external-payment UI).
//
// IMPORTANT — this flag does NOT decide test vs. real charges. That is 100%
// server-side: the create-checkout / stripe-webhook edge functions charge
// whatever the STRIPE_SECRET_KEY Supabase secret is set to.
//   • sk_test_… → test mode → the 4242 4242 4242 4242 card works (TestFlight).
//   • sk_live_… → live mode → real cards are charged (public launch).
// The client never holds a Stripe key, so switching test↔live is a secret swap
// + edge-function redeploy — no app rebuild needed.
//
// Apple context: App Review rejected v1.0(4) under Guideline 3.1.1 for selling
// these through Stripe, so the iPhone app now uses Apple In-App Purchase
// (src/iap.js, verify-iap edge function) and only the website uses Stripe.
// payments.js picks the rail per platform; this flag still hides both.
//
// NOTE: this only shows/hides the client-side purchase UI. Nothing is granted
// without a completed Stripe payment, so it can never be bypassed for free.
export const PAYMENTS_ENABLED =
  process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';

// ─── Demo mode (marketing screenshots) ───────────────────────────────────────
//
// When ON, the swipe feed is populated with a fixed set of hand-crafted sample
// listings from src/demoListings.js instead of querying Supabase. This exists
// solely to produce App Store / marketing screenshots on a real device without
// polluting the production database or creating fake auth accounts.
//
// OFF by default, so normal and production builds are completely unaffected.
// Build a screenshot build with:  REACT_APP_DEMO=true npm run sync
//
// The sample data never touches the database — it is injected client-side only,
// so there is nothing to clean up afterward. Just rebuild without the flag.
export const DEMO_MODE =
  process.env.REACT_APP_DEMO === 'true';

// ─── Google OAuth client IDs (native social sign-in) ─────────────────────────
//
// Used by src/socialAuth.js to initialize Google sign-in. Both are PUBLIC (not
// secrets) — safe to commit or ship in the bundle. Get them from Google Cloud
// Console → APIs & Services → Credentials (see the social-login runbook):
//   • WEB client ID  — the audience Supabase's Google provider validates against.
//   • iOS client ID  — what the on-device Google sheet uses (also drives the
//     reversed-client-ID URL scheme added to Info.plist).
// Override per-build with REACT_APP_GOOGLE_WEB_CLIENT_ID / _IOS_CLIENT_ID, or
// just paste the values in as the defaults below. Apple sign-in needs neither.
export const GOOGLE_WEB_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ||
  '639459712125-cc0hpcm5pchrfchgcl8fuj53tr9blgad.apps.googleusercontent.com';
export const GOOGLE_IOS_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID ||
  '639459712125-431m261pqdj777vl7lrt5hj18tapd51c.apps.googleusercontent.com';

// Reveal the Apple/Google buttons on the WEBSITE too (native app always shows
// them). Web uses Supabase's hosted OAuth redirect (supabase.auth.signInWithOAuth)
// which needs the web-side provider config done first (esp. the Apple Services ID
// + key) — see the social-sign-in runbook's web section. ON by default now that
// the web config is done (Sep 2026); set REACT_APP_WEB_SOCIAL=false to hide them.
export const WEB_SOCIAL_ENABLED =
  process.env.REACT_APP_WEB_SOCIAL !== 'false';
